import { Show, Suspense, createSignal } from "solid-js";
import { useParams, A, createAsync, type RouteDefinition } from "@solidjs/router";
import { Title } from "@solidjs/meta";
import { AppShell } from "~/components/layout";
import { Button, Badge, Skeleton, ScrollArea, Breadcrumbs } from "~/components/ui";
import { ReportContent } from "~/components/reports/ReportContent";
import { getReport } from "~/server/bff";
import { formatDate } from "~/lib/utils";
import {
  Download, Copy, Printer, FileText, Calendar, Loader2
} from "lucide-solid";

export const route = {
  preload: ({ params }) => getReport(params.id as string),
} satisfies RouteDefinition;

export default function ReportDetailPage() {
  const params = useParams();
  const report = createAsync(() => getReport(params.id || ""), { deferStream: true });
  const [downloadingPdf, setDownloadingPdf] = createSignal(false);
  const [downloadingMd, setDownloadingMd] = createSignal(false);

  const handleDownloadPdf = async () => {
    const r = report();
    if (!r?.content) return;
    
    setDownloadingPdf(true);
    try {
      // Create a simple HTML document for PDF generation
      // Note: For real PDF generation, you'd use a library like jsPDF or a server endpoint
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <title>${r.name}</title>
          <style>
            body { font-family: Georgia, serif; max-width: 800px; margin: 40px auto; padding: 20px; }
            h1 { font-size: 24px; margin-bottom: 10px; }
            .meta { color: #666; font-size: 14px; margin-bottom: 30px; }
            .content { line-height: 1.6; white-space: pre-wrap; }
          </style>
        </head>
        <body>
          <h1>${r.name}</h1>
          <div class="meta">${r.type} Report • Generated ${formatDate(r.createdAt)}</div>
          <div class="content">${r.content}</div>
        </body>
        </html>
      `;
      
      // Open in new window for printing to PDF
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(htmlContent);
        printWindow.document.close();
        printWindow.print();
      }
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleDownloadMarkdown = () => {
    const r = report();
    if (!r?.content) return;
    
    setDownloadingMd(true);
    try {
      const markdown = `# ${r.name}\n\n*${r.type} Report • Generated ${formatDate(r.createdAt)}*\n\n---\n\n${r.content}`;
      
      const blob = new Blob([markdown], { type: "text/markdown" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${r.name.replace(/[^a-z0-9]/gi, "_")}.md`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setDownloadingMd(false);
    }
  };

  const handleCopy = async () => {
    const r = report();
    if (r?.content) {
      await navigator.clipboard.writeText(r.content);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <AppShell>
      <Title>{report()?.name ?? "Report"} - IntelAnalyst</Title>
      <Suspense fallback={<div class="p-6"><ReportContentSkeleton /></div>}>
        <Show
          when={report()}
          fallback={
            <div class="p-6 max-w-2xl mx-auto space-y-4">
              <Breadcrumbs
                items={[
                  { label: "Track", href: "/track/reports" },
                  { label: "Reports", href: "/track/reports" },
                  { label: "Not Found" },
                ]}
                showHome
                class="mb-2"
              />
              <div class="space-y-2">
                <h1 class="text-2xl font-bold tracking-tight">Report not found</h1>
                <p class="text-sm text-muted-foreground">
                  This report may have been deleted, or you may not have access.
                </p>
              </div>
              <A href="/track/reports">
                <Button variant="outline">Back to Reports</Button>
              </A>
            </div>
          }
        >
          {(r) => (
            <div class="flex h-[calc(100vh-4rem)]">
              <div class="w-72 border-r flex flex-col bg-muted/30">
                <div class="p-4 border-b">
                  <Breadcrumbs
                    items={[
                      { label: "Track", href: "/track/reports" },
                      { label: "Reports", href: "/track/reports" },
                      { label: r().name },
                    ]}
                    showHome
                    class="mb-3"
                  />

                  <h2 class="font-semibold leading-tight">{r().name}</h2>
                  <div class="flex items-center gap-2 mt-2 text-sm text-muted-foreground">
                    <Calendar class="h-3 w-3" />
                    {formatDate(r().createdAt)}
                  </div>
                  <div class="flex gap-1 mt-2">
                    <Badge variant="outline" class="text-xs">{r().type}</Badge>
                    <Show when={r().sourceName}>
                      <Badge variant="secondary" class="text-xs">{r().sourceName}</Badge>
                    </Show>
                  </div>
                </div>

                <div class="flex-1" />

                <div class="p-4 border-t space-y-2">
                  <Button
                    variant="outline"
                    size="sm"
                    class="w-full justify-start"
                    onClick={handleDownloadPdf}
                    disabled={downloadingPdf()}
                  >
                    <Show when={downloadingPdf()} fallback={<Download class="h-4 w-4 mr-2" />}>
                      <Loader2 class="h-4 w-4 mr-2 animate-spin" />
                    </Show>
                    Download PDF
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    class="w-full justify-start"
                    onClick={handleDownloadMarkdown}
                    disabled={downloadingMd()}
                  >
                    <Show when={downloadingMd()} fallback={<Download class="h-4 w-4 mr-2" />}>
                      <Loader2 class="h-4 w-4 mr-2 animate-spin" />
                    </Show>
                    Download Markdown
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    class="w-full justify-start"
                    onClick={handleCopy}
                  >
                    <Copy class="h-4 w-4 mr-2" />
                    Copy to Clipboard
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    class="w-full justify-start"
                    onClick={handlePrint}
                  >
                    <Printer class="h-4 w-4 mr-2" />
                    Print
                  </Button>
                </div>
              </div>

              <div class="flex-1 overflow-hidden">
                <ScrollArea class="h-full">
                  <div class="max-w-3xl mx-auto p-8 print:p-4">
                    <article class="prose prose-slate dark:prose-invert max-w-none">
                      <div class="mb-8 pb-8 border-b not-prose">
                        <div class="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                          <FileText class="h-4 w-4" />
                          <span class="uppercase tracking-wider font-medium">
                            {r().type} Report
                          </span>
                        </div>
                        <h1 class="text-3xl font-bold tracking-tight mb-2">{r().name}</h1>
                        <p class="text-muted-foreground">
                          Generated on {formatDate(r().createdAt)}
                        </p>
                      </div>

                      <div class="report-content prose prose-slate dark:prose-invert max-w-none">
                        <ReportContent content={r().content} />
                      </div>
                    </article>
                  </div>
                </ScrollArea>
              </div>
            </div>
          )}
        </Show>
      </Suspense>
    </AppShell>
  );
}

function ReportContentSkeleton() {
  return (
    <div class="space-y-4">
      <Skeleton class="h-8 w-64" />
      <Skeleton class="h-4 w-32" />
      <div class="space-y-2 mt-8">
        <Skeleton class="h-4 w-full" />
        <Skeleton class="h-4 w-full" />
        <Skeleton class="h-4 w-3/4" />
      </div>
      <div class="space-y-2 mt-6">
        <Skeleton class="h-6 w-48" />
        <Skeleton class="h-4 w-full" />
        <Skeleton class="h-4 w-full" />
        <Skeleton class="h-4 w-2/3" />
      </div>
    </div>
  );
}
